import Zone from "@insite/client-framework/Components/Zone";
import { CartsDataViewContext } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import toggleFiltersOpen from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/ToggleFiltersOpen";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";

interface OwnProps extends WidgetProps {}

const mapDispatchToProps = {
    toggleFiltersOpen,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface SavedOrderListHeaderStyles {
    container?: GridContainerProps;
    filterToggleContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    emptyGridItem?: GridItemProps;
    toggleFilterGridItem?: GridItemProps;
    toggleFilterIcon?: IconPresentationProps;
    toggleFilterClickable?: ClickablePresentationProps;
    orderCountText?: TypographyPresentationProps;
}

export const savedOrderListHeaderStyles: SavedOrderListHeaderStyles = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 20px;
        `,
    },
    filterToggleContainer: {
        css: css`
            @media screen and (max-width: 767px) {
                > div > div {
                    justify-content: space-between !important;

                    button {
                        min-width: auto;
                    }
                }
            }
        `,
    },
    titleGridItem: {
        width: 12,
        style: { fontWeight: 600 },
    },
    toggleFilterGridItem: {
        width: 12,
        style: {
            justifyContent: "flex-start",
            alignSelf: "flex-start",
        },
        css: css`
            margin-bottom: 15px;

            &.filter-grid[data-test-selector="filter-grid"] {
                margin-bottom: 15px;
            }
        `,
    },
    toggleFilterIcon: {
        src: Filter,
        size: 18,
        css: css`
            display: inline-block;
            margin-right: 8px;
        `,
    },
    toggleFilterClickable: {
        css: css`
            padding-left: 15px;
            margin-left: 15px;
            border-left: 1px solid #E4E8F0;
            min-width: 123px;

            &.hide-btn {
                display: none;
            }

            span.label {
                color: #002554;
                font-family: 'Lato',sans-serif;
                font-size: 14px;
                letter-spacing: 0;
            }
        `,
    },
    orderCountText: {
        css: css`
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: 20px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 22px;
            white-space: nowrap;
        `,
    }
};
const styles = savedOrderListHeaderStyles;

const SavedOrderListHeader: FC<Props> = ({ toggleFiltersOpen, id }) => {
    const savedOrderListDataView = useContext(CartsDataViewContext);

    if (!savedOrderListDataView.value || !savedOrderListDataView.pagination) {
        return (
            <GridItem {...styles.titleGridItem}>
                <Zone contentId={id} zoneName="Content" />
            </GridItem>
        )
    }
    
    const { pagination } = savedOrderListDataView;

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.titleGridItem}>
                    <Zone contentId={id} zoneName="Content" />
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.filterToggleContainer}>
                <GridItem {...styles.toggleFilterGridItem} data-test-selector="filter-grid" className="filter-grid">
                    <Typography {...styles.orderCountText} data-test-selector="savedOrderList_resultCount">
                        {pagination?.totalItemCount} {translate("Saved Carts")}
                    </Typography>
                    <Clickable
                        {...styles.toggleFilterClickable}
                        onClick={() => toggleFiltersOpen()}
                        data-test-selector="savedOrderList_showHideFilters"
                    >
                        <Icon {...styles.toggleFilterIcon} />
                        <span className="label">{translate("Filter")}</span>

                    </Clickable>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(SavedOrderListHeader),
    definition: {
        group: "Saved Order List",
        displayName: "Header",
        allowedContexts: ["SavedOrderListPage"],
    },
};

export default widgetModule;
